import { Injectable } from '@angular/core';

import { AttachmentMapper } from 'ish-core/models/attachment/attachment.mapper';
import { AttributeGroup } from 'ish-core/models/attribute-group/attribute-group.model';
import { AttributeHelper } from 'ish-core/models/attribute/attribute.helper';
import { Attribute } from 'ish-core/models/attribute/attribute.model';
import { CategoryData } from 'ish-core/models/category/category.interface';
import { CategoryMapper } from 'ish-core/models/category/category.mapper';
import { ImageMapper } from 'ish-core/models/image/image.mapper';
import { Link } from 'ish-core/models/link/link.model';
import { Price } from 'ish-core/models/price/price.model';
import { VariationAttributeMapper } from 'ish-core/models/product-variation/variation-attribute.mapper';
import { SeoAttributesMapper } from 'ish-core/models/seo-attributes/seo-attributes.mapper';
import { WarrantyMapper } from 'ish-core/models/warranty/warranty.mapper';

import { SkuQuantityType } from './product.helper';
import { ProductData, ProductDataStub, ProductVariationLink } from './product.interface';
import { AllProductTypes, Product, VariationProduct, VariationProductMaster } from './product.model';

/**
 * check if attribute is available and return value, otherwise undefined
 */
function retrieveStubAttributeValue<T>(data: ProductDataStub, attributeName: string) {
  return data ? AttributeHelper.getAttributeValueByAttributeName<T>(data.attributes, attributeName) : undefined;
}

/**
 * CUSTOMIZATION: listing stubs may expose qty as a plain number (ICM Integer)
 * or as `{ value: number }`. Normalize both so PLP qty defaults to min order qty.
 */
function retrieveStubQuantityValue(data: ProductDataStub, attributeName: string): number | undefined {
  const raw = retrieveStubAttributeValue<number | { value: number }>(data, attributeName);
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return raw;
  }
  if (raw && typeof raw === 'object' && typeof raw.value === 'number') {
    return raw.value;
  }
  return undefined;
}

/** CUSTOMIZATION: MVP 2.4.7 — price-on-request from payload field or Boolean attribute. */
function retrievePriceOnRequestFlag(
  data: { priceOnRequest?: boolean; attributes?: Attribute[] } | undefined,
  stubAttributes?: Attribute[]
): boolean | undefined {
  if (data?.priceOnRequest === true) {
    return true;
  }
  const attrs = data?.attributes ?? stubAttributes;
  if (
    AttributeHelper.getAttributeValueByAttributeName<boolean>(attrs, 'priceOnRequest') === true ||
    AttributeHelper.getAttributeValueByAttributeName<boolean>(attrs, 'PriceOnRequest') === true
  ) {
    return true;
  }
  return undefined;
}

/**
 * maps promotion links data to a string array of promotion ids
 */
function mapPromotionIds(data: Link[]): string[] {
  return data ? data.map(promotionLink => promotionLink.itemId) : [];
}

/**
 * maps an attribute group and its attribute data to the same format as it is used in a single product call
 */
function mapAttributeGroups(data: ProductDataStub): Record<string, AttributeGroup> {
  return { [data.attributeGroup.name]: { attributes: data.attributeGroup.attributes } };
}

/**
 * Product Mapper maps data of HTTP requests to model instances and vice versa.
 */
@Injectable({ providedIn: 'root' })
export class ProductMapper {
  constructor(
    private imageMapper: ImageMapper,
    private attachmentMapper: AttachmentMapper,
    private categoryMapper: CategoryMapper,
    private variationAttributeMapper: VariationAttributeMapper
  ) {}

  static parseSkuFromURI(uri: string): string {
    const match = /products[^/]*\/([^?]*)/.exec(uri);
    if (match) {
      return decodeURIComponent(match[1]);
    } else {
      console.warn(`could not find sku in uri '${uri}'`);
      return;
    }
  }

  static findDefaultVariation(variationLinks: Link[]): string {
    const defaultVariation = variationLinks.find(variation =>
      AttributeHelper.getAttributeValueByAttributeName<boolean>(variation.attributes, 'defaultVariation')
    );

    return defaultVariation ? ProductMapper.parseSkuFromURI(defaultVariation.uri) : undefined;
  }

  static constructMasterStub(sku: string, variations: Partial<VariationProduct>[]): Partial<VariationProductMaster> {
    return variations?.length
      ? {
          type: 'VariationProductMaster',
          sku,
          completenessLevel: 0,
          variationAttributeValues: variations
            .map(v => v.variableVariationAttributes)
            .flat()
            .filter(
              (val, idx, arr) =>
                arr.findIndex(el => el.variationAttributeId === val.variationAttributeId && el.value === val.value) ===
                idx
            ),
        }
      : undefined;
  }

  fromLink(link: Link): Partial<Product> {
    return {
      sku: ProductMapper.parseSkuFromURI(link.uri),
      name: link.title,
      shortDescription: link.description,
      type: 'Product',
      completenessLevel: 1,
    };
  }

  fromVariationLink(link: ProductVariationLink, productMasterSKU: string): Partial<VariationProduct> {
    // CUSTOMIZATION: map optional list/sale price + lead time from variation link attributes
    // so PDP option chips can show price deltas (Presidio Baymard variation UX).
    // Also map availability + thumbnail so the Product Table can show honest stock/images.
    // Contract: PROVISIONAL until ICM confirms these link attribute names (handoff REVIEW/MERGE).
    const salePrice = AttributeHelper.getAttributeByAttributeName(link.attributes, 'salePrice')?.value as Price;
    const listPrice = AttributeHelper.getAttributeByAttributeName(link.attributes, 'listPrice')?.value as Price;
    const readyForShipmentMin = AttributeHelper.getAttributeByAttributeName(link.attributes, 'readyForShipmentMin')
      ?.value as number;
    const readyForShipmentMax = AttributeHelper.getAttributeByAttributeName(link.attributes, 'readyForShipmentMax')
      ?.value as number;
    const availability = AttributeHelper.getAttributeByAttributeName(link.attributes, 'availability')?.value as boolean;
    const inStock = AttributeHelper.getAttributeByAttributeName(link.attributes, 'inStock')?.value as boolean;
    const imageUrl = AttributeHelper.getAttributeByAttributeName(link.attributes, 'image')?.value as string;

    return {
      ...this.fromLink(link),
      variableVariationAttributes: this.variationAttributeMapper.fromData(
        link.variableVariationAttributeValuesExtended
      ),
      productMasterSKU,
      type: 'VariationProduct',
      failed: false,
      available: this.calculateAvailable(availability, inStock),
      ...(imageUrl ? { images: this.imageMapper.fromImageUrl(imageUrl) } : {}),
      ...(salePrice ? { salePrice } : {}),
      ...(listPrice ? { listPrice } : {}),
      ...(readyForShipmentMin != null ? { readyForShipmentMin } : {}),
      ...(readyForShipmentMax != null ? { readyForShipmentMax } : {}),
    };
  }

  fromRetailSetLink(link: Link): Partial<Product> {
    return {
      sku: ProductMapper.parseSkuFromURI(link.uri),
      name: link.title,
      shortDescription: link.description,
      completenessLevel: 1,
    };
  }
  /**
   * construct a {@link Product} stub from data returned by link list responses with additional data
   */
  fromStubData(data: ProductDataStub): Partial<AllProductTypes> {
    const sku = retrieveStubAttributeValue<string>(data, 'sku');
    if (!sku) {
      throw new Error('cannot construct product stub without SKU');
    }
    const productCategory = retrieveStubAttributeValue<CategoryData>(data, 'defaultCategory');

    const promotionLinks = retrieveStubAttributeValue<{ elements: Link[] }>(data, 'promotions')?.elements ?? [];

    const product: Partial<Product> = {
      shortDescription: data.description,
      name: data.title,
      sku,
      images: this.imageMapper.fromImageUrl(retrieveStubAttributeValue(data, 'image')),
      manufacturer: retrieveStubAttributeValue(data, 'manufacturer'),
      // CUSTOMIZATION: MVP 2.1.6 — manufacturer part number on listing stubs
      manufacturerSKU: retrieveStubAttributeValue(data, 'manufacturerSKU'),
      available: this.calculateAvailable(
        retrieveStubAttributeValue(data, 'availability'),
        retrieveStubAttributeValue(data, 'inStock')
      ),
      longDescription: undefined,
      // CUSTOMIZATION: accept plain Integer or `{ value }` for listing stub qty fields
      minOrderQuantity: retrieveStubQuantityValue(data, 'minOrderQuantity'),
      maxOrderQuantity: retrieveStubQuantityValue(data, 'maxOrderQuantity'),
      stepQuantity: retrieveStubQuantityValue(data, 'stepQuantity'),
      packingUnit: retrieveStubAttributeValue(data, 'packingUnit'),

      attributeGroups: data.attributeGroup && mapAttributeGroups(data),
      // CUSTOMIZATION: listing stubs may carry ETA so PLP can distinguish
      // backorder (OOS + ETA → orderable) from hard OOS (notify-only).
      readyForShipmentMin: retrieveStubAttributeValue(data, 'readyForShipmentMin'),
      readyForShipmentMax: retrieveStubAttributeValue(data, 'readyForShipmentMax'),
      roundedAverageRating: +retrieveStubAttributeValue<string>(data, 'roundedAverageRating') || 0,
      type: 'Product',
      // Guard incomplete mock stubs (string categoryRef / missing categoryPath) so list mapping
      // does not throw CategoryMapper "input is falsy".
      defaultCategoryId:
        productCategory &&
        Array.isArray((productCategory as CategoryData).categoryPath) &&
        (productCategory as CategoryData).categoryPath.length
          ? this.categoryMapper.fromDataSingle(productCategory).uniqueId
          : undefined,
      promotionIds: mapPromotionIds(promotionLinks),
      completenessLevel: 2,
      failed: false,

      /* DIAS CUSTOMIZATION START */

      salePrice: AttributeHelper.getAttributeByAttributeName(data.attributes, 'salePrice')?.value as Price,
      // CUSTOMIZATION: MVP 2.4.7
      ...(retrievePriceOnRequestFlag(undefined, data.attributes) ? { priceOnRequest: true } : {}),
      /* DIAS CUSTOMIZATION END */
    };
    return this.appendProductTypeForStubData(data, product);
  }
  /**
   * map API Response to fully qualified {@link Product}s
   */
  fromData(data: ProductData): AllProductTypes {
    const product: Product = {
      type: 'Product',
      name: data.productName,
      shortDescription: data.shortDescription,
      longDescription: data.longDescription,
      available: this.calculateAvailable(data.availability, data.inStock),
      minOrderQuantity: data.minOrderQuantity,
      maxOrderQuantity: data.maxOrderQuantity,
      stepQuantity: data.stepOrderQuantity,
      packingUnit: data.packingUnit,
      availableStock: data.availableStock,
      attributes: data.attributeGroups?.PRODUCT_DETAIL_ATTRIBUTES?.attributes || data.attributes || [],
      attributeGroups: data.attributeGroups,
      attachments: this.attachmentMapper.fromAttachments(data.attachments),
      images: this.imageMapper.fromImages(data.images),
      manufacturer: data.manufacturer,
      // CUSTOMIZATION: MVP 2.1.6 — manufacturer part number for listing cards
      manufacturerSKU: data.manufacturerSKU,
      readyForShipmentMin: data.readyForShipmentMin,
      readyForShipmentMax: data.readyForShipmentMax,
      roundedAverageRating: +data.roundedAverageRating || 0,
      numberOfReviews: data.numberOfReviews || 0,
      sku: data.sku,
      defaultCategoryId: data.defaultCategory
        ? this.categoryMapper.fromDataSingle(data.defaultCategory).uniqueId
        : undefined,
      promotionIds: mapPromotionIds(data.promotions),
      completenessLevel: 3,
      failed: false,
      seoAttributes: SeoAttributesMapper.fromData(data.seoAttributes),
      availableWarranties: WarrantyMapper.fromLinkData(data.availableWarranties),
      // CUSTOMIZATION: MVP 2.4.7 — price on request
      ...(retrievePriceOnRequestFlag(data) ? { priceOnRequest: true } : {}),
    };

    return this.appendProductTypeForData(data, product);
  }

  private calculateAvailable(availability: boolean, inStock: boolean) {
    return !!availability && (inStock !== undefined ? inStock : true);
  }

  /**
   * map product bundle API Response to a link / quantity type
   */
  fromProductBundleData(links: Link[]): SkuQuantityType[] {
    return links.map(link => ({
      sku: ProductMapper.parseSkuFromURI(link.uri),
      quantity: AttributeHelper.getAttributeValueByAttributeName<{ value: number }>(link.attributes, 'quantity').value,
    }));
  }

  private appendProductTypeForStubData(data: ProductDataStub, product: Partial<Product>): Partial<AllProductTypes> {
    const mastered = retrieveStubAttributeValue<boolean>(data, 'mastered');
    const productMaster = retrieveStubAttributeValue<boolean>(data, 'productMaster');
    const productMasterSKU = retrieveStubAttributeValue<string>(data, 'productMasterSKU');
    const retailSet = retrieveStubAttributeValue<boolean>(data, 'retailSet');

    if (productMaster) {
      return {
        ...product,
        type: 'VariationProductMaster',
      };
    } else if (mastered) {
      return {
        ...product,
        productMasterSKU,
        type: 'VariationProduct',
      };
    } else if (retailSet) {
      return {
        ...product,
        type: 'RetailSet',
      };
    } else {
      return product;
    }
  }

  private appendProductTypeForData(data: ProductData, product: Product): AllProductTypes {
    if (data.productMaster) {
      return {
        ...product,
        variationAttributeValues: this.variationAttributeMapper.fromMasterData(data.variationAttributeValuesExtended),
        type: 'VariationProductMaster',
      };
    } else if (data.mastered) {
      return {
        ...product,
        productMasterSKU: data.productMasterSKU,
        variableVariationAttributes: this.variationAttributeMapper.fromData(data.variationAttributeValuesExtended),
        type: 'VariationProduct',
      };
    } else if (data.productTypes?.includes('BUNDLE') || data.productBundle) {
      return {
        ...product,
        type: 'Bundle',
      };
    } else if (data.retailSet) {
      return {
        ...product,
        type: 'RetailSet',
      };
    } else {
      return product;
    }
  }
}
